import React from "react";
import { Loader2 } from "lucide-react";
import { useCustomerWizard } from "@/features/customerWizard/useCustomerWizard";
import PaidCard from "@/features/customerWizard/PaidCard";
import ExpiredCard from "@/features/customerWizard/ExpiredCard";
import WaitingCard from "@/features/customerWizard/WaitingCard";
import PendingReviewPrompt from "@/features/customerWizard/PendingReviewPrompt";
import ErrorCard from "@/features/customerWizard/ErrorCard";
import SessionCard from "@/features/customerWizard/SessionCard";
import PayOpeningOverlay from "@/features/customerWizard/PayOpeningOverlay";

/**
 * Iter-318 (P0) — signed-in customer flow on /engage/{qr}.
 *
 * Iter 632 — customer wizard split (behaviour IDENTICAL): ALL state,
 * effects and handlers moved into
 * src/features/customerWizard/useCustomerWizard.js; the render states
 * are one component each next to it. This file is only the layout shell.
 *
 * Creates the engagement session on load, shows the till short-code,
 * bill entry/confirmation, review requirement and Stripe checkout:
 *   POST /engagement/sessions {qr_code_id[, acknowledge_pending]}
 *   GET  /engagement/sessions/{id}            (5s poll)
 *   POST /engagement/sessions/{id}/confirm-bill   (bill arrives from the till — Iter-474)
 *   POST /engagement/sessions/{id}/skip-review
 *   POST /engagement/sessions/{id}/checkout   → {url} (Stripe)
 *   GET  /engagement/sessions/{id}/payment-status
 * Any 410 → "Session expired — scan the QR again".
 */
export default function EngageSessionPanel({
  qrCodeId,
  brandColor,
  textOnBrand,
  paidReturn = false,
  paidSessionId = null,
  resumeId = null,
}) {
  const w = useCustomerWizard({ qrCodeId, paidReturn, paidSessionId, resumeId });

  /* ── render states ─────────────────────────────────────────── */

  // Paid wins over everything (a returned Stripe session may already be
  // closed upstream — a 410 must not hide the confirmation).
  if (w.showPaid) return <PaidCard w={w} />;

  if (w.isExpired) return <ExpiredCard w={w} />;

  // Iter 694 — expired/missing token: sign in and come straight back to
  // this exact URL (the claim fires immediately on return). NEVER the
  // "ask the staff" card on a 401.
  if (w.needsLogin) {
    return (
      <div className="rounded-2xl bg-slate-50 border border-slate-200 p-5 text-center" data-testid="engage-signin-to-connect">
        <div className="font-bold text-slate-900 text-sm">Sign in to connect to the till</div>
        <p className="mt-1 text-xs text-slate-500">
          Your session timed out — sign in and this screen connects the moment you&apos;re back.
        </p>
        <a
          href={`/login?next=${encodeURIComponent(`/engage/${qrCodeId}`)}`}
          data-testid="engage-signin-to-connect-cta"
          style={{ backgroundColor: brandColor, color: textOnBrand }}
          className="mt-4 inline-flex items-center justify-center rounded-full font-bold py-3 px-8 text-sm shadow-card"
        >
          Sign in
        </a>
      </div>
    );
  }

  if (w.waiting && !w.sessionId) return <WaitingCard w={w} />;

  if (w.pending409) {
    return <PendingReviewPrompt w={w} brandColor={brandColor} textOnBrand={textOnBrand} />;
  }

  if (w.fatal) return <ErrorCard w={w} />;

  if (!w.s) {
    return (
      <div className="rounded-2xl bg-slate-50 border border-slate-200 p-8 grid place-items-center" data-testid="engage-session-loading">
        <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <>
      <SessionCard w={w} brandColor={brandColor} textOnBrand={textOnBrand} />
      {/* Iter 641 — instant "Opening secure payment…" cover while Stripe
          builds the checkout page (stays up through the redirect hop). */}
      <PayOpeningOverlay visible={w.payOpening} />
    </>
  );
}
